import { useState, type FormEvent } from 'react';
import { MIN_PASSWORD_LENGTH } from '../lib/password';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../lib/api';
import { usePortal } from '../lib/portal';
import { Alert, Button, Field, PasswordInput } from '../components/ui';
import '../styles/auth.css';

import { EnvBadge } from '../components/EnvBadge';
/**
 * Spending a reset link.
 *
 * The other half of "we've sent a reset link": the request screen existed and
 * promised an email, but nothing sent one and no page accepted a token, so
 * everybody who forgot a password was locked out with the product saying
 * otherwise. This is where the link lands.
 *
 * On success it sends the person to sign in rather than logging them in.
 * Resetting a password revokes every existing session by design, and an admin
 * still owes a second factor at the door; signing in is the one path that
 * handles both.
 */
export function ResetPassword() {
  const { siteHome } = usePortal();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_PASSWORD_LENGTH && password === confirm && !!token;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await apiRequest('/api/v1/auth/password-reset/confirm', {
        method: 'POST', auth: false, body: { token, password },
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      // A spent, expired or mistyped link is one case as far as the server is
      // concerned, and saying which would tell an attacker something.
      setError(err instanceof ApiError && err.status === 400
        ? 'That link has expired or has already been used. Ask for a new one.'
        : 'Could not reset the password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-brand-row">
        <Link to={siteHome} className="auth-brand">NyumbaSuite</Link>
        <EnvBadge />
      </div>
      <form className="auth-card login" onSubmit={submit} noValidate>
        <h1 className="auth-title">Choose a new password</h1>

        {!token ? (
          <>
            <Alert tone="danger">This link is missing its token. Open the link from your email exactly as it was sent.</Alert>
            <div className="auth-foot"><Link to="/forgot-password">Ask for a new link</Link></div>
          </>
        ) : done ? (
          <>
            <Alert tone="success">
              Password changed. Signing you in takes you to the sign-in page — every
              other session has been signed out.
            </Alert>
            <div className="auth-foot"><Link to="/login">Sign in now</Link></div>
          </>
        ) : (
          <>
            {error && <Alert tone="danger">{error}</Alert>}
            <p className="auth-lede">
              Pick something you don’t use anywhere else. At least {MIN_PASSWORD_LENGTH} characters.
            </p>
            <Field label="New password" htmlFor="password"
              error={tooShort ? `At least ${MIN_PASSWORD_LENGTH} characters.` : undefined}>
              <PasswordInput id="password" autoComplete="new-password" autoFocus
                value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            <Field label="Confirm new password" htmlFor="confirm"
              error={mismatch ? 'The two passwords do not match.' : undefined}>
              <PasswordInput id="confirm" autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </Field>
            <Button type="submit" block loading={busy} disabled={!canSubmit}>
              Change password
            </Button>
            <div className="auth-foot"><Link to="/login">Back to sign in</Link></div>
          </>
        )}
      </form>
      <Link className="auth-link" to={siteHome}>← Back to homepage</Link>
    </div>
  );
}
